import { memo } from 'react';
import type { StatusLevel } from '../strudel';

type Props = {
  message: string;
  level: StatusLevel;
};

const LEVEL_COLORS: Record<StatusLevel, string> = {
  '': 'text-zinc-500',
  ok: 'text-emerald-400',
  err: 'text-red-400',
  warn: 'text-orange-400',
};

// Memoized: App re-renders at 12fps during playback; the status bar
// only changes on discrete events (play, save, error), so skipping
// reconciliation keeps it idle the rest of the time.
export const StatusBar = memo(StatusBarInner);

function StatusBarInner({ message, level }: Props) {
  return (
    // safe-pb clears the iPhone home indicator in PWA standalone mode;
    // safe-pl/pr clear landscape notches. The bg-zinc-950 floods through
    // the inset area so the chassis still reads as edge-to-edge.
    <footer className="flex justify-between items-center gap-3 p-2 px-4 border-t border-zinc-900 bg-zinc-950 text-[11px] safe-pb safe-pl safe-pr">
      <div className={LEVEL_COLORS[level] + ' truncate'}>{message}</div>
      <div className="hidden sm:flex items-center gap-3 text-zinc-600 text-[10px] tracking-[0.18em] uppercase whitespace-nowrap">
        <span>⌘S save · ⌘↵ play</span>
        <span aria-hidden="true" className="text-zinc-800">·</span>
        <a
          href="https://github.com/blackfry/strudel-harness"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-zinc-300"
          title="AGPL-licensed source"
        >
          Source
        </a>
        <span aria-hidden="true" className="text-zinc-800">·</span>
        <a
          href="https://codeberg.org/uzu/strudel"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-zinc-300"
          title="The pattern language + audio engine this runs on"
        >
          Built on Strudel
        </a>
      </div>
    </footer>
  );
}
